/**
 * Input de curs valutar, 4 zecimale.
 *
 * Trei reguli care nu sunt negociabile, pentru ca fiecare corespunde unui esec silentios real:
 *
 *  1. Referinta BNR e PLACEHOLDER-FANTOMA, nu valoare. Blur-ul lasa campul GOL. Un curs lipsa
 *     nu devine niciodata `0` — altfel banca fara cotatie castiga orice comparatie.
 *  2. `Preia BNR` (`Alt+B`) completeaza intr-o singura actiune si stampileaza provenienta.
 *  3. Garda de plauzibilitate (±3% fata de BNR) e AVERTISMENT, niciodata blocaj: cursurile
 *     negociate difera legitim, iar o validare dura i-ar invata sa se lupte cu instrumentul.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { parseRoRate } from '@evomag/core/money';
import { formatRate } from '@evomag/core/format';

export type ProvenientaCurs = 'bnr' | 'negociat' | 'manual' | 'istoric';

export const ETICHETE_PROVENIENTA: Record<ProvenientaCurs, string> = {
  bnr: 'BNR',
  negociat: 'Negociat',
  manual: 'Manual',
  istoric: 'Istoric',
};

/** Peste atat, cursul introdus se semnaleaza (nu se refuza). */
const PRAG_PLAUZIBILITATE = 0.03;

const procentFmt = new Intl.NumberFormat('ro-RO', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: 'always',
});

export interface PropsRateInput {
  /** Cursul curent, scalat x1e6, sau `null` = necompletat (starea implicita a foii). */
  value: number | null;
  /** Referinta BNR pentru ziua blocului, scalata x1e6. `null` daca nu s-a sincronizat inca. */
  referintaBnr: number | null;
  onCommit: (rateE6: number | null, provenienta: ProvenientaCurs | null) => void;
  /** Nume accesibil: „Curs BCR, 13.08.2026". */
  eticheta: string;
  provenienta?: ProvenientaCurs | null;
  disabled?: boolean;
  className?: string;
}

export function RateInput({
  value,
  referintaBnr,
  onCommit,
  eticheta,
  provenienta = null,
  disabled = false,
  className,
}: PropsRateInput) {
  const idMesaj = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(() => (value === null ? '' : formatRate(value)));
  const [eroare, setEroare] = useState(false);
  const [prov, setProv] = useState<ProvenientaCurs | null>(provenienta);
  const [avertisment, setAvertisment] = useState<{ curs: number; abatere: number } | null>(null);
  const anterior = useRef<number | null>(value);

  useEffect(() => {
    if (document.activeElement !== ref.current) {
      setText(value === null ? '' : formatRate(value));
      setEroare(false);
    }
  }, [value]);

  function comita(cursE6: number | null, sursa: ProvenientaCurs | null): void {
    anterior.current = value;
    setProv(sursa);
    onCommit(cursE6, sursa);

    if (cursE6 !== null && referintaBnr !== null && referintaBnr > 0) {
      const abatere = (cursE6 - referintaBnr) / referintaBnr;
      setAvertisment(Math.abs(abatere) > PRAG_PLAUZIBILITATE ? { curs: cursE6, abatere } : null);
    } else {
      setAvertisment(null);
    }
  }

  function normalizeaza(): void {
    const brut = text.trim();
    if (brut === '') {
      setEroare(false);
      setAvertisment(null);
      if (value !== null) comita(null, null);
      return;
    }
    const curs = parseRoRate(brut);
    if (curs === null) {
      setEroare(true);
      return;
    }
    setEroare(false);
    setText(formatRate(curs));
    if (curs !== value) comita(curs, 'manual');
  }

  function preiaBnr(): void {
    if (referintaBnr === null) return;
    setText(formatRate(referintaBnr));
    setEroare(false);
    comita(referintaBnr, 'bnr');
    ref.current?.focus();
  }

  return (
    <span className={`inline-flex flex-col gap-0.5 ${className ?? ''}`}>
      <span className="inline-flex items-center gap-1">
        <input
          ref={ref}
          className="input num w-24 text-right"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={text}
          /* Indiciu, NU valoare: la blur campul rmne gol. */
          placeholder={referintaBnr === null ? '' : `${formatRate(referintaBnr)} BNR`}
          aria-label={eticheta}
          aria-invalid={eroare}
          aria-describedby={idMesaj}
          onChange={(e) => {
            setText(e.target.value);
            if (eroare) setEroare(false);
          }}
          onBlur={normalizeaza}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              normalizeaza();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setText(value === null ? '' : formatRate(value));
              setEroare(false);
            } else if (e.altKey && e.key.toLowerCase() === 'b') {
              e.preventDefault();
              preiaBnr();
            }
          }}
        />

        <button
          type="button"
          className="hit-24 chip text-ink-2"
          onClick={preiaBnr}
          disabled={disabled || referintaBnr === null}
          title={
            referintaBnr === null
              ? 'Referința BNR nu este încă sincronizată pentru această zi'
              : `Preia ${formatRate(referintaBnr)} de la BNR (Alt+B)`
          }
        >
          Preia BNR
        </button>

        {prov ? (
          <span className="badge" title={`Proveniență curs: ${ETICHETE_PROVENIENTA[prov]}`}>
            {ETICHETE_PROVENIENTA[prov]}
          </span>
        ) : null}
      </span>

      <span id={idMesaj} className={eroare ? 'text-11 text-neg' : 'doar-sr'} role="status">
        {eroare ? 'Curs invalid. Format: 5,2409' : 'Curs cu 4 zecimale; gol înseamnă necompletat'}
      </span>

      {avertisment ? (
        <span className="inline-flex flex-wrap items-center gap-2 text-11 text-ink-1" role="alert">
          <span
            data-stare="urgent"
            className="pastila"
            title="Abatere mare față de referința BNR"
          >
            atenție
          </span>
          <span>
            Curs <span className="num">{formatRate(avertisment.curs)}</span> diferă cu{' '}
            <span className="num">{procentFmt.format(avertisment.abatere)}</span> de BNR{' '}
            <span className="num">{formatRate(referintaBnr)}</span>. Continui?
          </span>
          <button type="button" className="hit-24 chip" onClick={() => setAvertisment(null)}>
            Continuă
          </button>
          <button
            type="button"
            className="hit-24 chip"
            onClick={() => {
              const inapoi = anterior.current;
              setText(inapoi === null ? '' : formatRate(inapoi));
              setAvertisment(null);
              onCommit(inapoi, inapoi === null ? null : 'manual');
            }}
          >
            Renunță
          </button>
        </span>
      ) : null}
    </span>
  );
}
